import React, {
  useEffect,
  useState,
  type ComponentType,
  type HTMLAttributes,
} from 'react';
import {
  PiCheck,
  PiDotsSixVerticalBold,
  PiEye,
  PiFile,
  PiImageSquare,
  PiMusicNotes,
  PiTrash,
  PiVideoCamera,
} from 'react-icons/pi';
import type { TCFile } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  inferPropertyMediaKindFromFile,
  type SupportedPropertyMediaKind,
} from 'src/modules/shared/utils/file/media-kind.util';

interface MediaPreviewCardProps {
  /** Files service item being previewed. */
  file: TCFile;
  /** Zero-based display index. */
  index: number;
  /** Whether this media item is currently selected. */
  selected?: boolean;
  /** Whether the selection action should be shown. */
  selectable?: boolean;
  /** Whether removal and drag actions should be enabled. */
  editable?: boolean;
  /** Persisted property-media kind fallback when the file lacks a MIME type. */
  fallbackKind?: SupportedPropertyMediaKind | null;
  /** Accessible label for the preview action. */
  previewLabel: string;
  /** Accessible label for the remove action. */
  removeLabel: string;
  /** Label for the select action. */
  selectLabel: string;
  /** Label shown when the item is already selected. */
  selectedLabel: string;
  /** Accessible label for the drag handle. */
  dragHandleLabel: string;
  /** Optional drag-handle component provided by the sortable wrapper. */
  DragHandle?: ComponentType<HTMLAttributes<HTMLDivElement>>;
  /** Called when the item selection is toggled. */
  onToggleSelection?: (fileId: string) => void;
  /** Called when the item is removed. */
  onRemove?: (fileId: string) => void;
}

const mediaKindIconByKind: Record<SupportedPropertyMediaKind, React.ReactNode> =
  {
    IMAGE: <PiImageSquare className="h-8 w-8" />,
    VIDEO: <PiVideoCamera className="h-8 w-8" />,
    AUDIO: <PiMusicNotes className="h-8 w-8" />,
    DOCUMENT: <PiFile className="h-8 w-8" />,
  };

/**
 * Shared media preview card used by property-media management and publication-media selection.
 */
export const MediaPreviewCard: React.FC<MediaPreviewCardProps> = ({
  file,
  index,
  selected = false,
  selectable = false,
  editable = false,
  fallbackKind,
  previewLabel,
  removeLabel,
  selectLabel,
  selectedLabel,
  dragHandleLabel,
  DragHandle,
  onToggleSelection,
  onRemove,
}) => {
  const fileId = file.id ?? '';
  const title = file.fileName ?? file.id ?? `file-${index + 1}`;
  const mediaKind = inferPropertyMediaKindFromFile(file, fallbackKind);
  const canPreview = Boolean(file.url);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [file.url]);

  const previewBody =
    mediaKind === 'IMAGE' && file.url && !hasImageError ? (
      <>
        <img
          src={file.url}
          alt={title}
          className={`h-full w-full object-cover transition-opacity duration-200 ${
            isImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setIsImageLoaded(true)}
          onError={() => setHasImageError(true)}
        />
        {!isImageLoaded ? (
          <div
            aria-hidden="true"
            className="absolute inset-0 animate-pulse bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.14),_transparent_55%),linear-gradient(160deg,_#f8fafc,_#e2e8f0)]"
          />
        ) : null}
      </>
    ) : mediaKind === 'VIDEO' && file.url ? (
      <video
        className="h-full w-full object-cover"
        muted
        playsInline
        preload="metadata"
      >
        <source src={file.url} type={file.contentType ?? undefined} />
      </video>
    ) : (
      <div className="flex h-full flex-col items-center justify-center gap-2 bg-slate-100 px-4 text-slate-500">
        {mediaKindIconByKind[mediaKind]}
        <Text size="tiny" className="text-slate-500">
          {file.contentType ?? title}
        </Text>
      </div>
    );

  return (
    <div
      className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
        selected
          ? 'border-primary-300 ring-2 ring-primary-100'
          : 'border-slate-200'
      }`}
    >
      <div className="relative aspect-[4/3] bg-slate-100">
        {previewBody}
        <div className="absolute left-3 top-3">
          <StatusPill variant="secondary" label={`#${index + 1}`} />
        </div>
        {DragHandle ? (
          <div className="absolute right-3 top-3">
            <DragHandle
              aria-label={dragHandleLabel}
              className="cursor-grab rounded-full bg-white/90 p-2 text-slate-500 shadow-sm transition hover:text-slate-700"
              title={dragHandleLabel}
            >
              <PiDotsSixVerticalBold className="h-4 w-4" />
            </DragHandle>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 px-4 py-4">
        <div className="min-w-0">
          <Text
            size="small"
            weight="medium"
            className="truncate text-slate-900"
          >
            {title}
          </Text>
          <Text size="tiny" className="mt-1 truncate text-slate-500">
            {file.contentType ?? 'application/octet-stream'}
          </Text>
        </div>

        <div className="flex items-center justify-between gap-3">
          {selectable && fileId ? (
            <Button
              type="button"
              variant={selected ? 'primary' : 'neutral'}
              appearance={selected ? 'soft' : 'outline'}
              size="sm"
              onClick={() => onToggleSelection?.(fileId)}
              className="whitespace-nowrap"
            >
              {selected ? selectedLabel : selectLabel}
            </Button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-1">
            {canPreview ? (
              <a
                href={file.url}
                target="_blank"
                rel="noreferrer"
                aria-label={previewLabel}
                title={previewLabel}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <PiEye className="h-4 w-4" />
              </a>
            ) : null}

            {editable && fileId ? (
              <Button
                type="button"
                variant="danger"
                appearance="ghost"
                size="sm"
                isIconOnly
                aria-label={removeLabel}
                title={removeLabel}
                onClick={() => onRemove?.(fileId)}
              >
                <PiTrash className="h-4 w-4" />
              </Button>
            ) : null}

            {selected ? (
              <div className="rounded-full bg-primary-100 p-2 text-primary-700">
                <PiCheck className="h-4 w-4" />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
